// ============================================================================
// DropdownMulti.jsx — Version Corelia complète (FormLayout + fermeture auto)
// ============================================================================
import React, { useState, useEffect, useRef } from "react";
import theme from "../../../styles/theme";
import "../../../styles/main.css";
import FormLayout from "../Forms/FormLayout";

const ChevronUp = "/assets/icones/chevron-up.svg";

const DropdownMulti = ({
  label,
  labelPosition = "left",
  labelWidth = null,
  align = "center",

  options = [],
  selectedValues = [],
  onChange,
  placeholder = "Sélectionnez...",
  disabled = false,

  width = "100%",
  height = 36,
  padding = `${theme.spacing.sm}px ${theme.spacing.md}px`,
  style = {},
}) => {

  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const isDisabled = disabled;

  // ---------------------------------------------------------------------------
  // Gestion fermeture auto (clic extérieur)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const handleOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  // ---------------------------------------------------------------------------
  // Fermeture ESC
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, []);

  // ---------------------------------------------------------------------------
  // Sélection / désélection
  // ---------------------------------------------------------------------------
  const toggleOption = (val) => {
    if (isDisabled) return;
    const exists = selectedValues.includes(val);
    const next = exists
      ? selectedValues.filter((v) => v !== val)
      : [...selectedValues, val];
    onChange(next);
  };

  const toggleSelectAll = () => {
    if (isDisabled) return;
    if (selectedValues.length === options.length) {
      onChange([]);
    } else {
      onChange(options.map((o) => o.value));
    }
  };

  // ---------------------------------------------------------------------------
  // Texte affiché dans le champ
  // ---------------------------------------------------------------------------
  const displayText =
    selectedValues.length > 0
      ? selectedValues
          .map((v) => options.find((o) => o.value === v)?.label ?? v)
          .join(", ")
      : placeholder;

  // ---------------------------------------------------------------------------
  return (
    <FormLayout
      label={label}
      labelPosition={labelPosition}
      labelWidth={labelWidth}
      align={align}
      width={width}
      style={style}
    >

      {/* Zone relative = pivot pour alignement parfait */}
      <div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>

        {/* Champ principal */}
        <div
          onClick={() => !isDisabled && setOpen(!open)}
          style={{
            width: "100%",
            height,
            padding,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderRadius: theme.borders.radius.sm,
            border: `1px solid ${theme.colors.borders.default}`,
            backgroundColor: isDisabled
              ? theme.buttons.disabled.background
              : theme.colors.background.input,
            cursor: isDisabled ? "not-allowed" : "pointer",
            fontFamily: theme.form.field.fontFamily,
            fontSize: theme.form.field.fontSize,
            color:
              selectedValues.length > 0
                ? theme.colors.text.default
                : "#7d97a1",
            fontStyle: selectedValues.length > 0 ? "normal" : "italic",
            boxSizing: "border-box",
            minHeight: 36,
          }}
        >
          <span
            style={{
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
          >
            {displayText}
          </span>

          {/* Chevron identique à DropdownList */}
          <img
            src={ChevronUp}
            alt="chevron"
            style={{
              width: 14,
              height: 14,
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
              opacity: disabled ? 0.4 : 0.8,
            }}
          />
        </div>

        {/* Liste déroulante multi */}
        {open && !isDisabled && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,              // Alignement parfait, comme DropdownList
              width: "100%",
              maxHeight: 200,
              overflowY: "auto",
              border: `1px solid ${theme.colors.borders.default}`,
              borderRadius: theme.borders.radius.sm,
              backgroundColor: theme.colors.background.surface,
              boxShadow: theme.shadows.md,
              zIndex: 9999,
              marginTop: 2,
            }}
          >

            {/* Tout sélectionner / désélectionner */}
            <div
              onClick={toggleSelectAll}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 8px",
                cursor: "pointer",
                backgroundColor: theme.colors.background.muted,
                borderBottom: `1px solid ${theme.colors.borders.default}`,
                fontWeight: theme.fontWeights.medium,
              }}
            >
              <input
                type="checkbox"
                checked={selectedValues.length === options.length && options.length > 0}
                readOnly
                style={{ cursor: "pointer", accentColor: theme.buttons.green.background }}
              />
              <span>
                {selectedValues.length === options.length
                  ? "Tout désélectionner"
                  : "Tout sélectionner"}
              </span>
            </div>

            {/* Options individuelles */}
            {options.map((opt) => {
              const checked = selectedValues.includes(opt.value);
              return (
                <div
                  key={opt.value}
                  onClick={() => toggleOption(opt.value)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 8px",
                    cursor: "pointer",
                    backgroundColor: checked
                      ? theme.colors.background.muted
                      : "transparent",
                    transition: "background-color 0.2s ease",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    readOnly
                    style={{
                      cursor: "pointer",
                      accentColor: theme.buttons.green.background,
                    }}
                  />
                  <span>{opt.label}</span>
                </div>
              );
            })}

          </div>
        )}
      </div>
    </FormLayout>
  );
};

export default DropdownMulti;
